import {
  handleCreateCallback,
  handleRedirectCallback,
  isAuthenticated,
  login,
  user as Auth0User,
  User,
  userRole,
} from '@/lib/AuthenticatorPlugin.ts'
import type { Router } from 'vue-router'
import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import checkAccountStatus from '@/composables/checkAccountStatus.ts'
import { useCheckUser } from '@/composables/users/checkUser.ts'
import { IUserStore } from '@/store/pinia/UserStore.ts'
import userService from '@/services/UserService.ts'
import { useRouter } from 'vue-router'

export const accountCreationGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  userStore: IUserStore,
) => {
  console.info(`[Guard] accountCreationGuard: isAuthenticated: ${isAuthenticated.value}`)
  if (!isAuthenticated.value) login()
  if (to.query.code && to.query.state) {
    const url = (await handleCreateCallback()) as string
    next(url)
  } else {
    console.info(`[Guard] redirecting to login`, to.name)
    if (to.name !== 'UserDetailsForm' && to.name !== 'CFSDisclaimer' && !userStore.getUser.auth0id)
      userStore.fetchUser()
    next()
  }
}

const adminUserRoles = ['superadmin', 'admin', 'callcentre', 'backoffice']
export const handleLoginCallback = async (router: Router) => {
  console.info(
    `[Guard] handleLoginCallback: isAuthenticated: ${isAuthenticated.value}`,
  )
  const redirectUrl = (await handleRedirectCallback()) as string
  const createKey = sessionStorage.getItem('create_key')
  const auth0id = Auth0User.value?.sub
  // call create freemium user endpoint, probably move this to a composable.
  if (createKey && auth0id) {
    console.log('creating freemium user')
    try {
      const res = await userService.createFreemiumUser(createKey, auth0id)
      if (res.status === 200) {
        sessionStorage.removeItem('create_key')
        router.push({ name: 'superInvestments' })
      }
    } catch (e: any) {
      console.error('error creating freemium user', e)
    }
  }

  const incompleteAccountRedirectUrl = await checkAccountStatus(Auth0User.value as User)

  if (adminUserRoles.includes(userRole.value)) {
    router.push(redirectUrl)
    return
  } else {
    try {
      router.push(incompleteAccountRedirectUrl.name ? incompleteAccountRedirectUrl : redirectUrl)
    } catch {
      throw new Error('error handling redirect callback')
    }
  }
}

export const adviceAuthGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  userStore: IUserStore,
): Promise<void> => {
  if (isAuthenticated.value) {
    const { isAdmin } = useCheckUser()
    console.info(
      `[Guard] adviceAuthGuard: isAuthenticated: ${isAuthenticated.value}, isAdmin: ${isAdmin()}`,
    )
    const router = useRouter()
    const adminPortalLoaded = router.hasRoute('admin.home')

    if (isAdmin()) {
      if (to.name && typeof to.name === 'string') {
        try {
          adminPortalLoaded ? next({ name: `admin.${to.name}` }) : next()
          return
        } catch (error) {
          console.warn(
            `No admin route found for 'admin.${to.name}', allowing user dashboard access`,
          )
        }
      }
      next()
      return
    }

    const user = userStore.getUser
    if (!user.auth0id) userStore.fetchUser()
    try {
      const incompleteAccountRedirectUrl = await checkAccountStatus(Auth0User.value as User)
      if (incompleteAccountRedirectUrl.name) next(incompleteAccountRedirectUrl)
      else next()
    } catch (e) {
      console.error('error adviceAuthGuard', e)
    }
  } else {
    login()
  }
}
